import { mergeReduce } from './merge';

import get from 'lodash/get';
import has from 'lodash/has';
import entries from 'lodash/entries';

import type { Parser, ParserConfig } from '../../types';

/**
 * Will create a parser (or a deserializer) which is a frozen object containing
 * a type (always `'parser'`), a name, and—most importantly—a parse function. It
 * allows you to reorganize and validate api responses.
 *
 * The most important part of the parser is the config ({@link ParserConfig}).
 * A config is a dictionary of fields that you would like in your
 * parsed/deserialized data. You can construct your config using the core fields:
 * {@link string}, {@link number}, {@link boolean}, {@link object}, {@link array},
 * or you can create your own fields ({@link createField}).
 *
 * @param {string} name - The name of your parser. Is available in the output.
 * @param {ParserConfig} config - The configuration for your parser
 * @return {Parser} Frozen parser object
 */
export default function createParser(
  name: string,
  config: ParserConfig,
): Parser {
  const parse = (data = {}) =>
    entries(config)
      .map(parseEntry(data))
      .reduce(mergeReduce);

  return Object.freeze({
    type: 'parser',
    name,
    parse,
  });
}

/**
 * Partially applies the data from an API response to a function used for mapping
 * over a {@link ParserConfig}.
 *
 * The returned function will handle the validation of the data and return the
 * parsed entry as an object to be merged with the other parsed objects to produce
 * the parsed data.
 *
 * @param {Object} data - The JSON response from an API
 * @return {function(Array): Object}
 */
function parseEntry(data) {
  return ([key, field]) => {
    const { iterable, nested, validate } = field;
    const path = field.path && field.path !== '' ? field.path : key;

    /* Checking if this is a computed field (i.e. we are constructing a new
     * nested object out of paths) by checking if the key exists in the data.
     * As paths are followed from the root, we pass the root data to the
     * validator.
     */
    const value =
      nested && !iterable && !has(data, path) ? data : get(data, path);

    const parsed = iterable
      ? Array.isArray(value)
        ? value.map(validate)
        : []
      : validate(value);

    return { [key]: parsed };
  };
}

/**
 * @typedef {Object} ParserConfig
 * @property {Field} key All values should be fields
 */

/**
 * @typedef {Object} Parser
 * @property {string} type This value is always 'parser'
 * @property {string} name The name given to this parser
 * @property {function(value: Object): Object} function The parse method
 */
